import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext, DragEndEvent, DragOverlay, DragStartEvent,
  PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import { Link } from "react-router-dom";
import { api, Lead, PRODUCT_LABEL, SOURCE_LABEL, STAGES } from "../lib/api";
import { LeadCardDraggable, LeadCardView } from "../components/LeadCard";
import { DroppableColumn } from "../components/Column";

export function Board() {
  const qc = useQueryClient();
  const leadsQ = useQuery({ queryKey: ["leads"], queryFn: () => api.listLeads(), refetchInterval: 15000 });
  const [activeLead, setActiveLead] = useState<Lead | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const setStage = useMutation({
    mutationFn: ({ id, stage }: { id: number; stage: string }) => api.setStage(id, stage),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leads"] }),
  });

  const byStage = useMemo(() => {
    const map: Record<string, Lead[]> = {};
    for (const s of STAGES) map[s.key] = [];
    for (const l of leadsQ.data || []) (map[l.stage] ||= []).push(l);
    return map;
  }, [leadsQ.data]);

  function onDragStart(e: DragStartEvent) {
    const id = Number(e.active.id);
    const l = (leadsQ.data || []).find((x) => x.lead_id === id) || null;
    setActiveLead(l);
  }

  function onDragEnd(e: DragEndEvent) {
    setActiveLead(null);
    if (!e.over) return;
    const overId = String(e.over.id);
    if (!overId.startsWith("col:")) return;
    const targetStage = overId.slice(4);
    const id = Number(e.active.id);
    const lead = (leadsQ.data || []).find((x) => x.lead_id === id);
    if (!lead || lead.stage === targetStage) return;
    setStage.mutate({ id, stage: targetStage });
  }

  if (leadsQ.isLoading) return <div>Загрузка…</div>;
  if (leadsQ.error) return <div className="error">Ошибка: {String(leadsQ.error)}</div>;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Воронка</h2>
        <div style={{ color: "var(--muted)", fontSize: 12 }}>
          Всего активных: {leadsQ.data?.filter((l) => l.stage !== "closed_lost" && l.stage !== "closed_won").length || 0}
        </div>
      </div>
      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="kanban">
          {STAGES.map((s) => (
            <DroppableColumn key={s.key} stageKey={s.key} title={s.title} count={byStage[s.key]?.length || 0}>
              {(byStage[s.key] || []).map((l) => (
                <Link key={l.lead_id} to={`/lead/${l.lead_id}`} style={{ color: "inherit" }}>
                  <LeadCardDraggable lead={l} productLabel={PRODUCT_LABEL[l.product]} sourceLabel={SOURCE_LABEL[l.source]} />
                </Link>
              ))}
            </DroppableColumn>
          ))}
        </div>
        <DragOverlay>
          {activeLead && (
            <LeadCardView lead={activeLead} productLabel={PRODUCT_LABEL[activeLead.product]} sourceLabel={SOURCE_LABEL[activeLead.source]} />
          )}
        </DragOverlay>
      </DndContext>
    </>
  );
}
